import React, { useState, useEffect } from 'react';
import { toast } from './Toast';
import { askConfirm } from './ui';
import { crmApi } from '../services/crmApi';
import { Sparkles, Brain, Shield, User, ChevronDown, ChevronUp, FileOutput, Download, FolderDown, Edit3, Save, X, RotateCcw, Tag, Eye } from 'lucide-react';
import { DocumentStudio } from './detail/DocumentStudio';

interface IndustryResult {
  industry: {
    id: string;
    name: string;
    category: string;
    clauses: string[];
    confidence: number;
    suggestedPKD?: string;
    templatePriority: Record<string, number>;
  };
  isOverridden?: boolean;
  clauses: Array<{ id: string; title: string; priority: number }>;
  clausesText: string;
  grammarPreview: Record<string, string> | null;
}

interface GrammarField {
  key: string;
  label: string;
  shortLabel: string;
}

const GRAMMAR_FIELDS: GrammarField[] = [
  { key: 'fullNameGenitive', label: 'Dopełniacz (kogo?)', shortLabel: 'Dopełniacz' },
  { key: 'fullNameDative', label: 'Celownik (komu?)', shortLabel: 'Celownik' },
  { key: 'fullNameInstrumental', label: 'Narzędnik (kim?)', shortLabel: 'Narzędnik' },
  { key: 'firstNameVocative', label: 'Wołacz (imię)', shortLabel: 'Wołacz' },
  { key: 'declarationEnding', label: 'Czasownik (oświadczył/a)', shortLabel: 'Czasownik' },
  { key: 'participantEnding', label: 'Uczestnik (zobowiązany/a)', shortLabel: 'Uczestnik' },
];

const CATEGORIES = ['Usługi', 'Handel', 'Produkcja', 'Transport', 'IT'];

interface Props {
  applicationId: number;
  pkdCode?: string;
  businessDescription?: string;
  firstName: string;
  lastName: string;
  onSmartDocumentGenerated?: () => void;
}

export const IndustryIntelligencePanel: React.FC<Props> = ({
  applicationId,
  pkdCode,
  businessDescription,
  firstName,
  lastName,
  onSmartDocumentGenerated,
}) => {
  const [result, setResult] = useState<IndustryResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showClauses, setShowClauses] = useState(false);
  const [showGrammar, setShowGrammar] = useState(false);
  const [generatingDoc, setGeneratingDoc] = useState(false);
  const [generatingBundle, setGeneratingBundle] = useState(false);
  const [smartDocTypes, setSmartDocTypes] = useState<Array<{ id: string; label: string; category: string; description: string }>>([]);
  const [selectedDocType, setSelectedDocType] = useState<string | null>(null);

  // Grammar editing state
  const [editingGrammar, setEditingGrammar] = useState(false);
  const [grammarEdits, setGrammarEdits] = useState<Record<string, string>>({});
  const [savingGrammar, setSavingGrammar] = useState(false);
  const [grammarSaved, setGrammarSaved] = useState(false);

  // Document Studio
  const [studioOpen, setStudioOpen] = useState(false);

  // Industry override editing state
  const [editingIndustry, setEditingIndustry] = useState(false);
  const [industryEdits, setIndustryEdits] = useState({ id: '', name: '', category: 'Usługi' });
  const [savingIndustry, setSavingIndustry] = useState(false);
  const [industrySaved, setIndustrySaved] = useState(false);
  const [isOverridden, setIsOverridden] = useState(false);
  const [allIndustries, setAllIndustries] = useState<Array<{ id: string; name: string; category: string }>>([]);

  // Auto-resolve when component mounts or PKD/description changes
  useEffect(() => {
    if (pkdCode || businessDescription) {
      handleResolve();
    }
  }, [pkdCode, businessDescription]);

  // Load available industries for dropdown
  useEffect(() => {
    const loadIndustries = async () => {
      try {
        const data = await crmApi.getIndustries();
        setAllIndustries(data.industries || []);
      } catch {}
    };
    loadIndustries();
  }, []);

  const handleResolve = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await crmApi.resolveIndustry({
        pkdCode: pkdCode || undefined,
        description: businessDescription || undefined,
        firstName,
        lastName,
        applicationId,
      });
      setResult(data);
      setIsOverridden(data.isOverridden || false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd rozpoznawania branży');
    } finally {
      setLoading(false);
    }
  };

  // === Industry override functions ===
  const startIndustryEdit = () => {
    if (!result?.industry) return;
    setIndustryEdits({
      id: result.industry.id,
      name: result.industry.name,
      category: result.industry.category,
    });
    setEditingIndustry(true);
    setIndustrySaved(false);
  };

  const cancelIndustryEdit = () => {
    setEditingIndustry(false);
  };

  const saveIndustryOverride = async () => {
    if (!industryEdits.name.trim()) {
      toast.error('Nazwa branży jest wymagana');
      return;
    }
    setSavingIndustry(true);
    try {
      await crmApi.saveIndustryOverride(applicationId, {
        id: industryEdits.id || industryEdits.name.toLowerCase().replace(/[^a-z0-9]/g, '_'),
        name: industryEdits.name.trim(),
        category: industryEdits.category,
      });

      // Update local state
      if (result) {
        setResult({
          ...result,
          industry: {
            ...result.industry,
            id: industryEdits.id,
            name: industryEdits.name.trim(),
            category: industryEdits.category,
            confidence: 1.0,
          },
          isOverridden: true,
        });
      }
      setIsOverridden(true);
      setEditingIndustry(false);
      setIndustrySaved(true);
      setTimeout(() => setIndustrySaved(false), 3000);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Błąd zapisu branży');
    } finally {
      setSavingIndustry(false);
    }
  };

  const resetIndustryOverride = async () => {
    if (!(await askConfirm({ title: 'Przywrócić automatyczne rozpoznawanie branży?' }))) return;
    setSavingIndustry(true);
    try {
      await crmApi.clearIndustryOverride(applicationId);
      setIsOverridden(false);
      setEditingIndustry(false);
      handleResolve();
      setIndustrySaved(true);
      setTimeout(() => setIndustrySaved(false), 3000);
    } catch (err: any) {
      toast.error('Błąd resetowania branży');
    } finally {
      setSavingIndustry(false);
    }
  };

  // === Grammar functions (unchanged) ===
  const startGrammarEdit = () => {
    if (!result?.grammarPreview) return;
    const edits: Record<string, string> = {};
    GRAMMAR_FIELDS.forEach(f => {
      edits[f.key] = result.grammarPreview?.[f.key] || '';
    });
    setGrammarEdits(edits);
    setEditingGrammar(true);
    setGrammarSaved(false);
  };

  const cancelGrammarEdit = () => {
    setEditingGrammar(false);
    setGrammarEdits({});
  };

  const saveGrammarOverride = async () => {
    setSavingGrammar(true);
    try {
      const overrides: Record<string, string> = {};
      GRAMMAR_FIELDS.forEach(f => {
        const edited = grammarEdits[f.key]?.trim();
        if (edited && edited !== result?.grammarPreview?.[f.key]) {
          overrides[f.key] = edited;
        }
      });

      await crmApi.saveGrammarOverride(applicationId, overrides);

      if (result?.grammarPreview) {
        const updated = { ...result.grammarPreview };
        GRAMMAR_FIELDS.forEach(f => {
          if (grammarEdits[f.key]?.trim()) {
            updated[f.key] = grammarEdits[f.key].trim();
          }
        });
        setResult({ ...result, grammarPreview: updated });
      }

      setEditingGrammar(false);
      setGrammarSaved(true);
      setTimeout(() => setGrammarSaved(false), 3000);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Błąd zapisu odmiany');
    } finally {
      setSavingGrammar(false);
    }
  };

  const resetGrammarOverride = async () => {
    if (!(await askConfirm({ title: 'Przywrócić automatyczną odmianę? Usuńie ręczne korekty.' }))) return;
    setSavingGrammar(true);
    try {
      await crmApi.clearGrammarOverride(applicationId);
      handleResolve();
      setEditingGrammar(false);
      setGrammarSaved(true);
      setTimeout(() => setGrammarSaved(false), 3000);
    } catch (err: any) {
      toast.error('Błąd resetowania odmiany');
    } finally {
      setSavingGrammar(false);
    }
  };

  // === Document generation (unchanged) ===
  const handleGenerateSmartDoc = async () => {
    if (!selectedDocType) return;
    setGeneratingDoc(true);
    try {
      const response = await crmApi.generateSmartDocument(applicationId, selectedDocType, {}, 'docx');
      const blob = new Blob([response.data], { type: String(response.headers['content-type'] ?? '') });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename="')[1]?.replace('"', '') || 'dokument_smart.docx'
        : 'dokument_smart.docx';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      onSmartDocumentGenerated?.();
    } catch (err: any) {
      setError('Błąd generowania dokumentu');
    } finally {
      setGeneratingDoc(false);
    }
  };

  const handleDownloadBundle = async () => {
    setGeneratingBundle(true);
    setError('');
    try {
      const response = await crmApi.generateSmartDocumentBundle(applicationId);
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dokumenty_wniosek_${applicationId}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      onSmartDocumentGenerated?.();
    } catch (err: any) {
      setError('Błąd generowania paczki dokumentów');
    } finally {
      setGeneratingBundle(false);
    }
  };

  // Load smart doc types
  useEffect(() => {
    const loadDocTypes = async () => {
      try {
        const types = await crmApi.getSmartDocTypes();
        setSmartDocTypes(types);
      } catch {}
    };
    loadDocTypes();
  }, []);

  const confidenceColor = (c: number) => {
    if (c >= 0.9) return 'text-green-600 bg-green-50';
    if (c >= 0.7) return 'text-blue-600 bg-blue-50';
    if (c >= 0.5) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const confidenceLabel = (c: number) => {
    if (c >= 0.9) return 'Pewna';
    if (c >= 0.7) return 'Wysoka';
    if (c >= 0.5) return 'Średnia';
    return 'Niska';
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold flex items-center text-lg">
          <Brain className="mr-2 text-purple-600" size={22} />
          Branżowy Mózg
        </h3>
        <button
          onClick={handleResolve}
          disabled={loading}
          className="text-sm bg-purple-100 text-purple-700 px-3 py-1.5 rounded-lg hover:bg-purple-200 disabled:opacity-50 flex items-center"
        >
          <Sparkles size={14} className="mr-1" />
          {loading ? 'Analizuję...' : 'Odśwież'}
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 p-2 rounded mb-3">{error}</div>
      )}

      {loading && (
        <div className="text-sm text-gray-500 animate-pulse py-4 text-center">
          Rozpoznawanie branży...
        </div>
      )}

      {result && !loading && (
        <div className="space-y-4">
          {/* Industry Detection — with Override UI */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                Wykryta branża
                {isOverridden && (
                  <span className="text-[10px] font-bold bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full">
                    Ręczna
                  </span>
                )}
                {industrySaved && <span className="text-green-600 text-xs">Zapisano!</span>}
              </span>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${confidenceColor(result.industry.confidence)}`}>
                {isOverridden ? 'Ręczna' : confidenceLabel(result.industry.confidence)} ({Math.round(result.industry.confidence * 100)}%)
              </span>
            </div>

            {editingIndustry ? (
              <div className="space-y-2">
                {/* Dropdown: known industries */}
                <select
                  value={industryEdits.id}
                  onChange={(e) => {
                    const selected = allIndustries.find(i => i.id === e.target.value);
                    if (selected) {
                      setIndustryEdits({ id: selected.id, name: selected.name, category: selected.category });
                    } else {
                      setIndustryEdits(prev => ({ ...prev, id: '' }));
                    }
                  }}
                  className="w-full text-sm p-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">-- Niestandardowa branża --</option>
                  {allIndustries.map(ind => (
                    <option key={ind.id} value={ind.id}>{ind.name} ({ind.category})</option>
                  ))}
                </select>

                {/* Custom name input */}
                <input
                  type="text"
                  placeholder="Nazwa branży (np. Usługi ogrodowe)"
                  value={industryEdits.name}
                  onChange={(e) => setIndustryEdits(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full text-sm p-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />

                {/* Category dropdown */}
                <select
                  value={industryEdits.category}
                  onChange={(e) => setIndustryEdits(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full text-sm p-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  {CATEGORIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>

                {/* Action buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={saveIndustryOverride}
                    disabled={savingIndustry}
                    className="flex items-center gap-1 text-xs px-3 py-1.5 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50 font-medium"
                  >
                    <Save size={12} /> {savingIndustry ? 'Zapisuję...' : 'Zapisz'}
                  </button>
                  <button
                    onClick={cancelIndustryEdit}
                    className="flex items-center gap-1 text-xs px-3 py-1.5 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <X size={12} /> Anuluj
                  </button>
                  {isOverridden && (
                    <button
                      onClick={resetIndustryOverride}
                      disabled={savingIndustry}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors ml-auto"
                    >
                      <RotateCcw size={12} /> Auto
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
                <p className="text-xl font-bold text-purple-800">{result.industry.name}</p>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-sm text-gray-600">
                    Kategoria: <span className="font-medium">{result.industry.category}</span>
                    {result.industry.suggestedPKD && (
                      <> | PKD: <span className="font-medium">{result.industry.suggestedPKD}</span></>
                    )}
                  </p>
                  <button
                    onClick={startIndustryEdit}
                    className="flex items-center gap-1 text-xs px-2 py-1 bg-white/70 text-purple-700 rounded hover:bg-white transition-colors"
                  >
                    <Edit3 size={12} /> Zmień branżę
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Legal Clauses */}
          <div>
            <button
              onClick={() => setShowClauses(!showClauses)}
              className="w-full flex items-center justify-between text-sm font-semibold text-gray-700 bg-gray-50 p-3 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <span className="flex items-center">
                <Shield className="mr-2 text-blue-600" size={16} />
                Klauzule prawne ({result.clauses.length})
              </span>
              {showClauses ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {showClauses && (
              <div className="mt-2 space-y-2 max-h-64 overflow-y-auto">
                {result.clauses.map((clause) => (
                  <div key={clause.id} className="flex items-center justify-between text-sm p-2 bg-blue-50 rounded">
                    <span className="text-gray-800">{clause.title}</span>
                    <span className="text-xs text-blue-600 font-medium">Priorytet: {clause.priority}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Grammar Preview (Editable) */}
          {result.grammarPreview && (
            <div>
              <button
                onClick={() => setShowGrammar(!showGrammar)}
                className="w-full flex items-center justify-between text-sm font-semibold text-gray-700 bg-gray-50 p-3 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <span className="flex items-center">
                  <User className="mr-2 text-green-600" size={16} />
                  Odmiana: {result.grammarPreview.formalAddressFull}
                  {grammarSaved && <span className="ml-2 text-green-600 text-xs">Zapisano!</span>}
                </span>
                {showGrammar ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {showGrammar && (
                <div className="mt-2">
                  <div className="flex gap-2 mb-2">
                    {!editingGrammar ? (
                      <button
                        onClick={startGrammarEdit}
                        className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors"
                      >
                        <Edit3 size={12} /> Edytuj odmianę
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={saveGrammarOverride}
                          disabled={savingGrammar}
                          className="flex items-center gap-1 text-xs px-2 py-1 bg-green-50 text-green-700 rounded hover:bg-green-100 transition-colors disabled:opacity-50"
                        >
                          <Save size={12} /> {savingGrammar ? 'Zapisuję...' : 'Zapisz'}
                        </button>
                        <button
                          onClick={cancelGrammarEdit}
                          className="flex items-center gap-1 text-xs px-2 py-1 bg-gray-50 text-gray-600 rounded hover:bg-gray-100 transition-colors"
                        >
                          <X size={12} /> Anuluj
                        </button>
                        <button
                          onClick={resetGrammarOverride}
                          disabled={savingGrammar}
                          className="flex items-center gap-1 text-xs px-2 py-1 bg-orange-50 text-orange-700 rounded hover:bg-orange-100 transition-colors ml-auto"
                        >
                          <RotateCcw size={12} /> Auto
                        </button>
                      </>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {GRAMMAR_FIELDS.map(field => (
                      <div key={field.key} className="p-2 bg-green-50 rounded">
                        <span className="text-gray-500 text-xs">{field.shortLabel}</span>
                        {editingGrammar ? (
                          <input
                            type="text"
                            title={field.label}
                            placeholder={field.shortLabel}
                            value={grammarEdits[field.key] || ''}
                            onChange={(e) => setGrammarEdits(prev => ({ ...prev, [field.key]: e.target.value }))}
                            className="w-full mt-0.5 px-1.5 py-0.5 text-sm border border-green-300 rounded focus:ring-1 focus:ring-green-500 focus:border-green-500"
                          />
                        ) : (
                          <p className="font-medium">{result.grammarPreview?.[field.key] || '—'}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Smart Document Generator */}
          <div className="border-t pt-4">
            <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
              <FileOutput className="mr-2 text-indigo-600" size={16} />
              Smart dokument (branża + klauzule + odmiana)
            </p>
            <div className="flex gap-2">
              <select
                value={selectedDocType || ''}
                onChange={(e) => setSelectedDocType(e.target.value || null)}
                className="flex-1 text-sm p-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Wybierz szablon...</option>
                {smartDocTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
              <button
                onClick={() => selectedDocType && setStudioOpen(true)}
                disabled={!selectedDocType}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 flex items-center"
              >
                <Eye size={14} className="mr-1" />
                Studio
              </button>
            </div>
            <button
              onClick={handleDownloadBundle}
              disabled={generatingBundle}
              className="mt-2 w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 flex items-center justify-center"
            >
              <FolderDown size={14} className="mr-2" />
              {generatingBundle ? 'Generuję paczkę...' : `Pobierz wszystkie wzory (${smartDocTypes.length} dok.)`}
            </button>
          </div>
        </div>
      )}

      {/* Initial state - no result yet */}
      {!result && !loading && !error && (
        <div className="text-sm text-gray-500 text-center py-4">
          {pkdCode || businessDescription ? (
            <button onClick={handleResolve} className="text-purple-600 hover:underline">
              Kliknij aby rozpoznać branżę
            </button>
          ) : (
            'Brak kodu PKD ani opisu działalności'
          )}
        </div>
      )}

      {/* Document Studio */}
      {studioOpen && selectedDocType && (
        <DocumentStudio
          applicationId={applicationId}
          docType={selectedDocType}
          docLabel={smartDocTypes.find(t => t.id === selectedDocType)?.label || selectedDocType}
          onClose={() => setStudioOpen(false)}
        />
      )}
    </div>
  );
};
